import { useEffect, useRef, useState } from 'react';

import { DataManager, WebApiAdaptor } from '@syncfusion/ej2-data';
import { ColumnChooser, GridComponent, Inject, Selection, Sort, Toolbar, FilterSettingsModel, Filter } from '@syncfusion/ej2-react-grids';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';
import { v4 as uuidV4 } from 'uuid';

import { getAvaDataManager } from 'common/AVADataManager';
import {
    NetworkMappingGridColumns,
    tabsToolbarOptions,
} from 'components/ProviderData/IpaAdministration/atoms/IpaAdministration.Metadata';
import { IPADetail, IIpaNetworkMapping, IpaNetworkMappingSelectedData, IIpaNetworkMappingSubmitResponse } from 'components/ProviderData/IpaAdministration/atoms/Models';
import { IpaNetworkMappingForm } from 'components/ProviderData/IpaAdministration/organisms/IpaNetworkMapping/IpaNetworkMappingForm';
import { IpaNetworkMappingService } from 'components/ProviderData/subjects/IpaNetworkMappingService';
import { IpaPreferredStatusService } from 'components/ProviderData/subjects/IpaPreferredStatusService';
import { dialogService } from 'subjects/common/DialogService';

type NetworkMappingProps = {
    ipa: IPADetail;
    isReadOnlyViewer: boolean;
};

const IpaNetworkMappingTab = (props: NetworkMappingProps) => {
    const toastRef = useRef<ToastComponent>(null);
    const networkMappingGridRef = useRef<GridComponent>(null);

    const [currentTabId] = useState<string>(uuidV4());
    const [currentTabIpa] = useState<IPADetail>(props.ipa);

    const [network, setNetwork] = useState<IIpaNetworkMapping | null>();
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [dataSource, setDataSource] = useState<DataManager>();

    const filterOptions: FilterSettingsModel = {
        type: 'Excel'
    };

    const saveNetworkMappingButtons = [
        {
            buttonModel: {
                disabled: props.isReadOnlyViewer,
                content: 'Save',
                iconCss: 'e-icons e-save-icon',
                cssClass: 'e-submit',
            },
            click: () => {
                IpaNetworkMappingService.submit();
            },
        },
        {
            buttonModel: {
                disabled: props.isReadOnlyViewer,
                content: 'Cancel',
                iconCss: 'e-icons e-cancel-icon',
                cssClass: 'e-cancel',
            },
            click: () => {
                dialogService.close();
                setNetwork(null);
            },
        },
    ];

    const updateDataSource = () => {
        if (!currentTabIpa.ipaCode) return;

        const dm = getAvaDataManager(
            `/api/IpaWebNetworkMapping/GetNetworkMappingList?ipaCode=${currentTabIpa.ipaCode}`,
            WebApiAdaptor,
        );
        setDataSource(dm);
    };

    const toolbarHandler = (args: any) => {
        if (args.item.id === 'refresh') {
            updateDataSource();
        }
        if (args.item.id === 'create') {
            IpaNetworkMappingService?.select({} as IIpaNetworkMapping, currentTabId);
        }
    };

    function handleFormDirtyChange(isDirty) {
        setIsDirty(isDirty);
    }

    useEffect(() => {
        if (networkMappingGridRef && props.isReadOnlyViewer) {
            networkMappingGridRef.current?.toolbarModule.enableItems(['create'], false);
        }
        if (network) {
            const dialogHeader = network.networkId ? 'Edit ' : 'Save ';
            const dialog: any = {};
            dialog.header = dialogHeader + 'Network & Agreement';
            dialog.buttons = saveNetworkMappingButtons;
            dialog.content = () => (
                <IpaNetworkMappingForm
                    network={network}
                    ipa={currentTabIpa}
                    onFormDirtyChange={handleFormDirtyChange}
                    isReadOnlyViewer={props.isReadOnlyViewer}
                />
            );
            dialog.width = 800;
            dialog.height = 570;
            dialogService.open(dialog);
            setNetwork(null);
        }
    }, [network, isDirty]);

    useEffect(() => {
        updateDataSource();

        IpaNetworkMappingService
            .get$()
            .subscribe(({ ipaNetworkMappingItem, tabId }: IpaNetworkMappingSelectedData) => {
                if(tabId === currentTabId && ipaNetworkMappingItem){
                    setNetwork(ipaNetworkMappingItem)
                }
            });

        const subscription = IpaNetworkMappingService.submit$().subscribe((response) => {
            if (response) {
                const defaultErrorMessage = 'An error occurred while saving the IpaWeb Network Mapping.';

                if(response.requestFailed) {
                    toastRef.current?.show({
                        title: 'Error',
                        content: response.requestErrorMessage || defaultErrorMessage,
                        cssClass: 'e-toast-danger',
                        timeOut: 15000
                    });
                    return;
                }

                const operationResult = response.data as IIpaNetworkMappingSubmitResponse;

                if(operationResult.bothOperationsFailed) {
                    toastRef.current?.show({
                        title: 'Error',
                        content: operationResult.errorMessage || defaultErrorMessage,
                        cssClass: 'e-toast-danger',
                        timeOut: 15000
                    });
                }
                else{
                    if(!operationResult.preferredStatusMappingOperationResult.processFailed &&
                        operationResult.preferredStatusMappingOperationResult.processedEntity) {
                        toastRef.current?.show({
                            title: 'Success!',
                            content: 'Default PreferredStatusMapping created for IpaWeb Network Mapping.',
                            cssClass: 'e-toast-success',
                            timeOut: 15000
                        });

                        if(currentTabIpa.id){
                            IpaPreferredStatusService.refreshGrid(currentTabIpa.id);
                        }
                    }
                    else if(operationResult.preferredStatusMappingOperationResult.processFailed) {
                        toastRef.current?.show({
                            title: 'Warning',
                            content: operationResult
                                .preferredStatusMappingOperationResult
                                .errorMessage || defaultErrorMessage,
                            cssClass: 'e-toast-warning',
                            timeOut: 15000
                        });
                    }

                    if (!operationResult.networkMappingOperationResult.processFailed &&
                        operationResult.networkMappingOperationResult.processedEntity) {
                        toastRef.current?.show({
                            title: 'Success!',
                            content: 'IpaWeb Network Mapping saved successfully.',
                            cssClass: 'e-toast-success',
                            timeOut: 15000
                        });
                        updateDataSource();
                    }
                    else if(operationResult.networkMappingOperationResult.processFailed) {
                        toastRef.current?.show({
                            title: 'Error',
                            content: operationResult.networkMappingOperationResult.errorMessage || defaultErrorMessage,
                            cssClass: 'e-toast-danger',
                            timeOut: 15000
                        });
                    }
                }
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, []);

    return (
        <div className='tab-content tabDiv p-3'>
            <div className='grid-container'>
                <GridComponent
                    id='ipaNetworkMappingGrid'
                    height={450}
                    allowPaging={true}
                    allowSorting={true}
                    columns={NetworkMappingGridColumns}
                    dataSource={dataSource}
                    toolbar={tabsToolbarOptions as any}
                    recordClick={(data) => {
                        IpaNetworkMappingService?.select(data.rowData, currentTabId);
                    }}
                    ref={networkMappingGridRef}
                    toolbarClick={toolbarHandler}
                    showColumnChooser={true}
                    filterSettings={filterOptions}
                    allowFiltering={true}
                    actionBegin={(args) => {
                        if (args.requestType == 'filterchoicerequest' || (args.requestType === 'filterSearchBegin' && !args.value)) {
                            args.filterChoiceCount = 50000;
                        }
                    }}
                >
                    <Inject services={[Selection, Sort, Toolbar, ColumnChooser, Filter]} />
                </GridComponent>
            </div>

            <ToastComponent
                id='toast_target'
                ref={toastRef}
                title='Login Account'
                content=''
                position={{ X: 'Right', Y: 'Top' }}
            />
        </div>
    );
};

export default IpaNetworkMappingTab;
